import React, { useState, useRef } from 'react';

import { colors } from '@alixpartners/ui-components';
import {
	Wrapper,
	Row,
	SortArrow,
	SortIconSpace,
	HeaderCellWrapper,
	SpanWrapper,
	RowSpan,
	SpanColumn,
	HeaderCell,
	HeaderText,
	HeaderCellNumber,
	CellSmall,
	CellIcon,
	Text,
	HeaderScrollWrapper,
	Content,
	HeaderCellSort,
	SpanTextWrapper,
	HeaderLineText,
	CellSpacing,
} from './ResultTable.styles';
import { VirtualList } from './VirtualList';
import { ITEMS_TYPE } from '../../utils';
import orderBy from 'lodash/orderBy';

const SORT_DIRECTION = {
	ASC: 'asc',
	DESC: 'desc',
};

export const ResultTable = ({
	data,
	companies,
	itemsType,
	spendFormatter,
	showCompanySpend,
	onItemsChange,
	currencySymbol,
	fullData,
	wacc,
	segments,
	showTopVendors,
}) => {
	const [sort, setSort] = useState({});
	const wrapperRef = useRef();
	const headerRef = useRef();

	const renderSortArrow = column => {
		if (!sort || sort.column !== column) {
			return <SortIconSpace />;
		}

		return (
			<SortArrow
				iconName="baseline_arrow_forward"
				iconSize={13}
				reverse={sort.direction === SORT_DIRECTION.DESC}
			/>
		);
	};

	const onSortChange = (column, isCompany = false) => {
		let newSort = { ...sort };

		if (sort.column === column) {
			if (sort.direction === SORT_DIRECTION.ASC) {
				newSort.direction = SORT_DIRECTION.DESC;
			} else {
				newSort = {};
			}
		} else {
			newSort = {
				column,
				direction: SORT_DIRECTION.ASC,
				isCompany,
			};
		}

		setSort(newSort);
	};

	const getSortedData = () => {
		const func = item => {
			return sort.isCompany
				? item.companies[sort.column] || 0
				: item[sort.column];
		};

		return orderBy(data, func, sort.direction);
	};

	const handleRemoveItem = ({ name, groupName }) => {
		onItemsChange(
			data.filter(item => item.name !== name || item.groupName !== groupName)
		);
	};

	const handleInputChange = update => {
		onItemsChange(
			data.map(item => {
				if (item.id === update.id) {
					return { ...item, ...update };
				}

				return item;
			})
		);
	};

	const getTotalSpend = companyName => {
		return data.reduce((acc, item) => {
			const spend =
				(companyName ? item.companies[companyName] : item.spend) || 0;
			return acc + spend;
		}, 0);
	};

	const getSegmentSpend = segmentName => {
		return data.reduce((acc, item) => {
			if (!item.segments) {
				return acc;
			}

			return acc + (item.segments[segmentName] || 0);
		}, 0);
	};

	const getSegmentPropValue = (segmentName, propName) => {
		return data.reduce((acc, item) => {
			if (!item.tailSegmentsLowHigh || !item.segments) {
				return acc;
			}

			const currentSegment =
				item.tailSegmentsLowHigh.find(item => item.name === segmentName) || {};
			const value =
				((item.segments[segmentName] || 0) * (currentSegment[propName] || 0)) /
				100;

			return acc + value;
		}, 0);
	};

	const getSegmentPropPercentage = (segmentName, propName) => {
		const segmentSpend = getSegmentSpend(segmentName);
		const propValue = getSegmentPropValue(segmentName, propName);

		if (segmentSpend === 0) {
			return '-';
		}

		const percentage = (propValue / segmentSpend) * 100;

		return `${Math.round(percentage)}%`;
	};
	const renderPT = () => {
		return (
			<React.Fragment>
				<HeaderCellNumber onClick={() => onSortChange('spend')}>
					Total spend{renderSortArrow('spend')}
				</HeaderCellNumber>
				{showCompanySpend &&
					companies.map(companyName => (
						<HeaderCellNumber
							key={companyName}
							onClick={() => onSortChange(companyName, true)}
							title={companyName}
						>
							<Text>{companyName}</Text>
							{renderSortArrow(companyName)}
						</HeaderCellNumber>
					))}
				{itemsType === ITEMS_TYPE.PT_HARMONIZATION && (
					<HeaderCellSort onClick={() => onSortChange('targetDpo')}>
						<HeaderText>Harmonized DPO</HeaderText>
						{renderSortArrow('targetDpo')}
					</HeaderCellSort>
				)}
				{itemsType === ITEMS_TYPE.PT_IMPROVEMENT && (
					<React.Fragment>
						<HeaderCellSort onClick={() => onSortChange('weightedAverageDpo')}>
							<HeaderText>Wt. Avg. DPO</HeaderText>
							{renderSortArrow('weightedAverageDpo')}
						</HeaderCellSort>
						<HeaderCellSort>
							<HeaderText>Target DPO</HeaderText>
						</HeaderCellSort>
					</React.Fragment>
				)}
				<SpanWrapper>
					<RowSpan>
						<HeaderText>Potential realizable</HeaderText>
					</RowSpan>
					<RowSpan>
						<SpanColumn>
							<HeaderText>Low%</HeaderText>
						</SpanColumn>
						<SpanColumn>
							<HeaderText>High%</HeaderText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
				<SpanWrapper>
					<RowSpan>
						<HeaderText>Working capital impact</HeaderText>
					</RowSpan>
					<RowSpan>
						<SpanColumn>
							<HeaderText>Low</HeaderText>
						</SpanColumn>
						<SpanColumn>
							<HeaderText>High</HeaderText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
			</React.Fragment>
		);
	};

	const renderDefault = () => {
		return (
			<React.Fragment>
				<HeaderCellNumber onClick={() => onSortChange('spend')}>
					{itemsType === ITEMS_TYPE.CATEGORY ? 'Spend' : 'Total spend'}
					{renderSortArrow('spend')}
				</HeaderCellNumber>
				{showCompanySpend &&
					companies.map(companyName => (
						<HeaderCellNumber
							key={companyName}
							onClick={() => onSortChange(companyName, true)}
							title={companyName}
						>
							<Text>{companyName}</Text>
							{renderSortArrow(companyName)}
						</HeaderCellNumber>
					))}
				<SpanWrapper>
					<RowSpan>
						<HeaderText>Add %</HeaderText>
					</RowSpan>
					<RowSpan>
						<SpanColumn>
							<HeaderText>Value</HeaderText>
						</SpanColumn>
						<SpanColumn>
							<HeaderText>%</HeaderText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
				<SpanWrapper>
					<RowSpan>
						<HeaderText>Low %</HeaderText>
					</RowSpan>
					<RowSpan>
						<SpanColumn>
							<HeaderText>Value</HeaderText>
						</SpanColumn>
						<SpanColumn>
							<HeaderText>%</HeaderText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
				<SpanWrapper>
					<RowSpan>
						<HeaderText>High %</HeaderText>
					</RowSpan>
					<RowSpan>
						<SpanColumn>
							<HeaderText>Value</HeaderText>
						</SpanColumn>
						<SpanColumn>
							<HeaderText>%</HeaderText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
				<CellSmall>
					<HeaderText>OPEX</HeaderText>
				</CellSmall>
				<CellSmall>
					<HeaderText>CAPEX</HeaderText>
				</CellSmall>
			</React.Fragment>
		);
	};

	const renderTailSpend = () => {
		return (
			<React.Fragment>
				<SpanWrapper onClick={() => onSortChange('spend')}>
					<RowSpan>
						<SpanTextWrapper>
							Total tail spend{renderSortArrow('spend')}
						</SpanTextWrapper>
					</RowSpan>
					<RowSpan>
						<HeaderLineText>{spendFormatter(getTotalSpend())}</HeaderLineText>
					</RowSpan>
				</SpanWrapper>
				{showCompanySpend &&
					companies.map(companyName => (
						<SpanWrapper
							key={companyName}
							onClick={() => onSortChange(companyName, true)}
							title={companyName}
						>
							<RowSpan onClick={() => onSortChange('spend')}>
								<SpanTextWrapper>
									<Text>{companyName}</Text>
									{renderSortArrow(companyName)}
								</SpanTextWrapper>
							</RowSpan>
							<RowSpan>
								<HeaderLineText>
									{spendFormatter(getTotalSpend(companyName))}
								</HeaderLineText>
							</RowSpan>
						</SpanWrapper>
					))}

				<SpanWrapper>
					<RowSpan>
						<HeaderText>Add %</HeaderText>
					</RowSpan>
					<RowSpan>
						<SpanColumn>
							<HeaderText>Value</HeaderText>
						</SpanColumn>
						<SpanColumn>
							<HeaderText>%</HeaderText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
				<SpanWrapper>
					<RowSpan>
						<HeaderText>Low %</HeaderText>
					</RowSpan>
					<RowSpan>
						<SpanColumn>
							<HeaderText>Value</HeaderText>
						</SpanColumn>
						<SpanColumn>
							<HeaderText>%</HeaderText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
				<SpanWrapper>
					<RowSpan>
						<HeaderText>High %</HeaderText>
					</RowSpan>
					<RowSpan>
						<SpanColumn>
							<HeaderText>Value</HeaderText>
						</SpanColumn>
						<SpanColumn>
							<HeaderText>%</HeaderText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
				{segments &&
					segments.map(({ name: segmentName }, index) => {
						const bgColor = index % 2 === 0 && colors.grey1;

						return (
							<React.Fragment key={segmentName}>
								<SpanWrapper title={segmentName} bgColor={bgColor}>
									<RowSpan>
										<SpanColumn>
											<Text color={colors.grey8}>{segmentName}</Text>
										</SpanColumn>
									</RowSpan>
									<RowSpan>
										<HeaderLineText>
											{spendFormatter(getSegmentSpend(segmentName))}
										</HeaderLineText>
									</RowSpan>
								</SpanWrapper>
								<SpanWrapper bgColor={bgColor}>
									<RowSpan>
										<HeaderLineText color={colors.grey5}>Low</HeaderLineText>
									</RowSpan>
									<RowSpan>
										<SpanColumn>
											<HeaderLineText color={colors.grey5}>
												{getSegmentPropPercentage(segmentName, 'lowPercentage')}
											</HeaderLineText>
										</SpanColumn>
										<SpanColumn>
											<HeaderLineText>
												{spendFormatter(
													getSegmentPropValue(segmentName, 'lowPercentage')
												)}
											</HeaderLineText>
										</SpanColumn>
									</RowSpan>
								</SpanWrapper>
								<SpanWrapper bgColor={bgColor} borderColor={colors.grey6}>
									<RowSpan>
										<HeaderLineText color={colors.grey5}>High</HeaderLineText>
									</RowSpan>
									<RowSpan>
										<SpanColumn>
											<HeaderLineText color={colors.grey5}>
												{getSegmentPropPercentage(
													segmentName,
													'highPercentage'
												)}
											</HeaderLineText>
										</SpanColumn>
										<SpanColumn>
											<HeaderLineText>
												{spendFormatter(
													getSegmentPropValue(segmentName, 'highPercentage')
												)}
											</HeaderLineText>
										</SpanColumn>
									</RowSpan>
								</SpanWrapper>
							</React.Fragment>
						);
					})}
				<CellSmall
					borderColor={colors.grey6}
					bgColor={segments.length % 2 === 0 && colors.grey1}
				>
					<HeaderText>OPEX</HeaderText>
				</CellSmall>
				<CellSmall bgColor={segments.length % 2 === 1 && colors.grey1}>
					<HeaderText>CAPEX</HeaderText>
				</CellSmall>
			</React.Fragment>
		);
	};

	const displayTopVendors =
		itemsType === ITEMS_TYPE.PT_IMPROVEMENT ||
		itemsType === ITEMS_TYPE.CATEGORY ||
		itemsType === ITEMS_TYPE.TAIL_SPEND;

	return (
		<Wrapper>
			<HeaderScrollWrapper innerRef={headerRef}>
				<Row noHover>
					<HeaderCell>
						<HeaderCellWrapper onClick={() => onSortChange('name')}>
							Item name
							{renderSortArrow('name')}
						</HeaderCellWrapper>
					</HeaderCell>

					{itemsType === ITEMS_TYPE.PT_IMPROVEMENT ||
					itemsType === ITEMS_TYPE.PT_HARMONIZATION
						? renderPT()
						: itemsType === ITEMS_TYPE.TAIL_SPEND
							? renderTailSpend()
							: renderDefault()}

					{!displayTopVendors ? <CellIcon /> : <CellSpacing />}
				</Row>
			</HeaderScrollWrapper>
			<Content innerRef={wrapperRef}>
				<VirtualList
					wacc={wacc}
					itemsType={itemsType}
					wrapperRef={wrapperRef}
					headerRef={headerRef}
					data={getSortedData()}
					companies={companies}
					spendFormatter={spendFormatter}
					showCompanySpend={showCompanySpend}
					onRemove={handleRemoveItem}
					onInputChange={handleInputChange}
					currencySymbol={currencySymbol}
					fullData={fullData}
					segments={segments}
					showTopVendors={showTopVendors}
				/>
			</Content>
		</Wrapper>
	);
};
